import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { 
  Trophy, Calendar, Users, MessageSquare, UserPlus, UserMinus,
  Loader2, Settings, ChevronRight, Award, Share2, Heart, TrendingUp,
  Target, Flame, BarChart3, Gamepad2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import SocialShare from "../components/SocialShare";

const BADGE_ICONS = {
  trophy: Trophy,
  crown: Trophy,
  star: Award,
  heart: Heart,
  medal: Award,
  zap: Award,
  users: Users,
  gamepad2: Trophy,
  shield: Award,
  "trending-up": Trophy,
};

export default function Profile() {
  const { username } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [badges, setBadges] = useState([]);
  const [detailedStats, setDetailedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // overview, stats, leagues

  const isOwnProfile = currentUser?.username === username;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profileRes = await api.getUser(username);
        setProfile(profileRes.data);
        setFollowing(profileRes.data.is_following);
        
        const leaguesRes = await api.getUserLeagues(profileRes.data.user_id);
        setLeagues(leaguesRes.data);
        
        // Fetch badges
        const badgesRes = await api.getUserBadges(profileRes.data.user_id);
        setBadges(badgesRes.data.badges || []);
        
        // Fetch detailed stats
        try {
          const statsRes = await api.getUserDetailedStatistics(profileRes.data.user_id);
          setDetailedStats(statsRes.data);
        } catch (e) {
          console.log("Stats not available");
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [username]);

  const handleFairPlayNomination = async () => {
    if (!currentUser) {
      toast.error("Giriş yapmanız gerekiyor");
      return;
    }
    
    try {
      const response = await api.nominateFairPlay(profile.user_id);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hata oluştu");
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      toast.error("Giriş yapmanız gerekiyor");
      return;
    }
    
    try {
      if (following) {
        await api.unfollow(profile.user_id);
        setFollowing(false);
        setProfile(p => ({ ...p, followers_count: p.followers_count - 1 }));
        toast.success("Takipten çıkıldı");
      } else {
        await api.follow(profile.user_id);
        setFollowing(true);
        setProfile(p => ({ ...p, followers_count: p.followers_count + 1 }));
        toast.success("Takip edildi");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <p className="text-[#B1C1C1]">Kullanıcı bulunamadı</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
      {/* Profile Header */}
      <div className="glass-card p-8 mb-8">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#4DFFFF]/20 to-[#4DFFFF]/5 flex items-center justify-center border-2 border-[#4DFFFF]/30">
            {profile.picture ? (
              <img src={profile.picture} alt={profile.name} className="w-full h-full rounded-full object-cover" />
            ) : (
              <span className="text-4xl font-bold text-[#4DFFFF]">
                {profile.name?.charAt(0)}
              </span>
            )}
          </div>
          
          <div className="flex-1">
            <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              {profile.name}
            </h1>
            <p className="text-[#64748B] text-lg">@{profile.username}</p>
            {profile.bio && (
              <p className="text-[#B1C1C1] mt-2">{profile.bio}</p>
            )}
            
            <div className="flex items-center gap-6 mt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#4DFFFF]">{profile.leagues_count}</p>
                <p className="text-xs text-[#64748B]">Lig</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{profile.followers_count}</p>
                <p className="text-xs text-[#64748B]">Takipçi</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{profile.following_count}</p>
                <p className="text-xs text-[#64748B]">Takip</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            {isOwnProfile ? (
              <Link to="/settings">
                <Button variant="outline" className="border-white/20 text-white">
                  <Settings className="w-4 h-4 mr-2" />
                  Düzenle
                </Button>
              </Link>
            ) : (
              <>
                <Button
                  onClick={handleFollow}
                  className={following 
                    ? "bg-white/10 text-white hover:bg-white/20" 
                    : "bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90"
                  }
                  data-testid="follow-btn"
                >
                  {following ? (
                    <>
                      <UserMinus className="w-4 h-4 mr-2" />
                      Takipten Çık
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Takip Et
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleFairPlayNomination}
                  variant="outline"
                  className="border-[#F61225]/50 text-[#F61225] hover:bg-[#F61225]/10"
                  data-testid="fair-play-btn"
                  title="Fair Play rozeti için oy ver"
                >
                  <Heart className="w-4 h-4" />
                </Button>
                <Link to={`/messages/${profile.user_id}`}>
                  <Button variant="outline" className="border-white/20 text-white">
                    <MessageSquare className="w-4 h-4" />
                  </Button>
                </Link>
              </>
            )}
            <Button
              onClick={() => setShareDialogOpen(true)}
              variant="outline"
              className="border-[#1DA1F2]/50 text-[#1DA1F2] hover:bg-[#1DA1F2]/10"
              data-testid="share-profile-btn"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Badges Section */}
      {badges.length > 0 && (
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-[#FFD700]" />
              <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                Rozetler
              </h2>
            </div>
            <Link to="/badges" className="text-sm text-[#4DFFFF] hover:underline">
              Tümünü Gör
            </Link>
          </div>
          <div className="flex flex-wrap gap-3">
            {badges.slice(0, 6).map((badge) => {
              const IconComponent = BADGE_ICONS[badge.icon] || Award;
              return (
                <div
                  key={badge.badge_id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5"
                  title={badge.description}
                >
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${badge.color}33` }}
                  >
                    <IconComponent className="w-4 h-4" style={{ color: badge.color }} />
                  </div>
                  <span className="text-sm text-white font-medium">{badge.name_tr}</span>
                </div>
              );
            })}
            {badges.length > 6 && (
              <Link 
                to="/badges"
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10"
              >
                <span className="text-sm text-[#64748B]">+{badges.length - 6} daha</span>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Detailed Statistics Section */}
      {detailedStats && (
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="w-5 h-5 text-[#4DFFFF]" />
            <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Detaylı İstatistikler
            </h2>
          </div>
          
          {/* Summary Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-white/5 rounded-lg text-center">
              <Trophy className="w-6 h-6 text-[#FFD700] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{detailedStats.summary?.total_wins || 0}</p>
              <p className="text-xs text-[#64748B]">Toplam Galibiyet</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg text-center">
              <Target className="w-6 h-6 text-[#4DFFFF] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{detailedStats.summary?.win_rate || 0}%</p>
              <p className="text-xs text-[#64748B]">Kazanma Oranı</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg text-center">
              <Flame className="w-6 h-6 text-[#F61225] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{detailedStats.summary?.best_win_streak || 0}</p>
              <p className="text-xs text-[#64748B]">En İyi Seri</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg text-center">
              <Award className="w-6 h-6 text-[#C6A87C] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{detailedStats.summary?.total_podiums || 0}</p>
              <p className="text-xs text-[#64748B]">Podyum</p>
            </div>
          </div>
          
          {/* Game Performance */}
          {detailedStats.game_performance?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#C6A87C] uppercase tracking-wider mb-3">Oyun Bazlı Performans</h3>
              <div className="space-y-3">
                {detailedStats.game_performance.slice(0, 4).map((game) => (
                  <div key={game.game_id} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                    <Gamepad2 className="w-8 h-8 text-[#4DFFFF]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{game.game_name}</p>
                      <p className="text-xs text-[#64748B]">{game.total_weeks} hafta oynandı</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#4DFFFF]">{game.wins}</p>
                      <p className="text-xs text-[#64748B]">galibiyet</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-white">{game.avg_rank?.toFixed(1)}</p>
                      <p className="text-xs text-[#64748B]">ort. sıra</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Best/Worst Week */}
          <div className="grid md:grid-cols-2 gap-4">
            {detailedStats.best_week && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-semibold text-green-400">En İyi Hafta</span>
                </div>
                <p className="text-white font-medium">{detailedStats.best_week.league_name}</p>
                <p className="text-sm text-[#64748B]">{detailedStats.best_week.points} puan</p>
              </div>
            )}
            {detailedStats.worst_week && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-red-400 rotate-180" />
                  <span className="text-sm font-semibold text-red-400">En Zor Hafta</span>
                </div>
                <p className="text-white font-medium">{detailedStats.worst_week.league_name}</p>
                <p className="text-sm text-[#64748B]">{detailedStats.worst_week.points} puan</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leagues */}
      <div className="glass-card overflow-hidden">
        <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Ligler
            </h2>
          </div>
        </div>
        
        {leagues.length > 0 ? (
          <div className="divide-y divide-white/5">
            {leagues.map((league) => (
              <Link
                key={league.league_id}
                to={`/league/${league.league_id}`}
                className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#C6A87C]/20 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-[#C6A87C]" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{league.name}</p>
                    <p className="text-sm text-[#64748B]">{league.game_name} • {league.players?.length} oyuncu</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#64748B]" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Trophy className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#B1C1C1]">Henüz lig yok</p>
          </div>
        )}
      </div>

      {/* Social Share Dialog */}
      {shareDialogOpen && (
        <SocialShare
          type="profile"
          id={username}
          title={profile?.name}
          onClose={() => setShareDialogOpen(false)}
        />
      )}
    </div>
  );
}
